import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import "./MultiFactorAuth.scss";
import mascot from "../img/mfa.png";
import { checkAuth } from "../../../awsAuth";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://cvanto-api.onrender.com"
    : "http://localhost:5000");

const API = `${API_BASE}/api/mfa`;

const MultiFactorAuth = () => {
  const [loading, setLoading] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [step, setStep] = useState("status");

  const [secret, setSecret] = useState("");
  const [otpUri, setOtpUri] = useState("");
  const [code, setCode] = useState("");

  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [copied, setCopied] = useState(false);

  const getAuthToken = () => {
    try {
      return localStorage.getItem("authToken") || null;
    } catch {
      return null;
    }
  };

  const ensureToken = async () => {
    let t = getAuthToken();
    if (!t) {
      const ok = await checkAuth();
      if (ok) t = getAuthToken();
    }
    return t;
  };

  const apiGet = async (url) => {
    const token = await ensureToken();
    const r = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    });
    if (r.status === 401) throw new Error("UNAUTHORIZED");
    return r.json();
  };

  const apiPost = async (url, body) => {
    const token = await ensureToken();
    const r = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify(body || {}),
    });
    if (r.status === 401) throw new Error("UNAUTHORIZED");
    return r.json();
  };

  const refreshStatus = async () => {
    setErr("");
    try {
      const data = await apiGet(`${API}/status`);
      setEnabled(!!data.enabled);
    } catch (e) {
      setEnabled(false);
      setErr(
        e.message === "UNAUTHORIZED"
          ? "Ви не авторизовані. Увійдіть знову."
          : "Не вдалося отримати статус MFA."
      );
    }
  };

  useEffect(() => {
    refreshStatus();
  }, []);

  const startSetup = async () => {
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      const data = await apiGet(`${API}/setup`);
      const base32 = String(data?.base32 || "").trim();
      const uri = String(data?.otpauthUrl || "").trim();
      if (!base32 || !uri) throw new Error("NO_SECRET");
      setSecret(base32);
      setOtpUri(uri);
      setStep("setup");
      setInfo(
        "📲 Для увімкнення відкрийте Google Authenticator (або інший TOTP-додаток), додайте акаунт Cvanto та введіть 6-значний код нижче."
      );
    } catch (e) {
      setErr(
        e.message === "UNAUTHORIZED"
          ? "❌ Ви не авторизовані. Увійдіть знову."
          : "❌ Не вдалося згенерувати секрет TOTP."
      );
    } finally {
      setLoading(false);
    }
  };

  const verifyAndEnable = async () => {
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      if (!code || code.length < 6) {
        setErr("Введіть 6-значний код.");
        return;
      }
      const data = await apiPost(`${API}/verify`, { code });
      if (data?.ok) {
        setStep("status");
        setCode("");
        setSecret("");
        setOtpUri("");
        setCopied(false);
        await refreshStatus();
      } else {
        setErr("Код невірний. Перевірте час на пристрої та спробуйте ще раз.");
      }
    } catch (e) {
      setErr(
        e.message === "UNAUTHORIZED"
          ? "❌ Ви не авторизовані. Увійдіть знову."
          : "❌ Не вдалося підтвердити код."
      );
    } finally {
      setLoading(false);
    }
  };

  const disableMfa = async () => {
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      const data = await apiPost(`${API}/disable`);
      if (data?.ok) {
        await refreshStatus();
      } else setErr("❌ Не вдалося вимкнути MFA.");
    } catch (e) {
      setErr(
        e.message === "UNAUTHORIZED"
          ? "❌ Ви не авторизовані. Увійдіть знову."
          : "❌ Помилка при вимкненні MFA."
      );
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      if (secret) {
        await navigator.clipboard.writeText(secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }
    } catch {
      /* ignore */
    }
  };

  const goBackToStatus = () => {
    setStep("status");
    setCode("");
    setSecret("");
    setOtpUri("");
    setCopied(false);
  };

  return (
    <div className={`mfa ${enabled ? "mfa--on" : "mfa--off"}`}>
      <div className="mfa-bg" />

      <div className="mfa-inner">
        {}
        <div
          className={`mfa-status-pill ${
            enabled ? "mfa-status-pill--on" : "mfa-status-pill--off"
          }`}
        >
          {enabled ? "Увімкнено" : "Вимкнено"}
        </div>

        {}
        {(info || err) && (
          <div className="mfa-banners">
            {info && <div className="banner info">{info}</div>}
            {err && <div className="banner error">{err}</div>}
          </div>
        )}

        {}
        <div className="mfa-hero">
          <div className="mfa-aura">
            <div className="mfa-orbit mfa-orbit--outer">
              <span className="orbit-dot orbit-dot--1" />
              <span className="orbit-dot orbit-dot--2" />
              <span className="orbit-dot orbit-dot--3" />
              <span className="orbit-dot orbit-dot--6" />
              <span className="orbit-dot orbit-dot--7" />
              <span className="orbit-dot orbit-dot--8" />
              <span className="orbit-dot orbit-dot--9" />
            </div>
            <div className="mfa-orbit mfa-orbit--inner">
              <span className="orbit-dot orbit-dot--4" />
              <span className="orbit-dot orbit-dot--5" />
              <span className="orbit-dot orbit-dot--10" />
              <span className="orbit-dot orbit-dot--11" />
              <span className="orbit-dot orbit-dot--12" />
            </div>
          </div>

          <div className="mfa-shield">
            <img
              className={`shield-img ${enabled ? "shield-img--on" : "shield-img--off"}`}
              src={mascot}
              alt="MFA shield"
            />
          </div>
        </div>

        {}
        {step === "status" && (
          <section className="mfa-copy">
            {enabled ? (
              <>
                <h2 className="mfa-title">Двофакторну автентифікацію увімкнено</h2>
                <p className="mfa-text">
                  Тепер при вході в Cvanto, окрім пароля, потрібно буде вводити
                  6-значний код з додатку Authenticator. Якщо хтось знає ваш
                  пароль, без коду він не зможе зайти у ваш профіль.
                </p>
                <button
                  className="btn primary mfa-cta"
                  onClick={disableMfa}
                  disabled={loading}
                  aria-busy={loading}
                >
                  {loading ? "Застосовую…" : "Вимкнути MFA"}
                </button>
              </>
            ) : (
              <>
                <h2 className="mfa-title">Підніміть захист акаунта</h2>
                <p className="mfa-text">
                  Додайте другий фактор — одноразовий код з застосунку Google
                  Authenticator або іншого TOTP-додатку. Це суттєво підсилює
                  безпеку вашого акаунта Cvanto.
                </p>
                <button
                  className="btn primary mfa-cta"
                  onClick={startSetup}
                  disabled={loading}
                  aria-busy={loading}
                >
                  {loading ? "Генерую секрет…" : "Увімкнути MFA"}
                </button>
              </>
            )}
          </section>
        )}

        {}
        {step === "setup" && (
          <section className="mfa-setup">
            <div className="setup-layout">
              <aside className="instructions-card">
                <h3>Як увімкнути двофакторну автентифікацію</h3>
                <ol>
                  <li>
                    Відкрийте <b>Google Authenticator</b> або інший TOTP-додаток.
                  </li>
                  <li>
                    Натисніть <b>«+»</b> і оберіть <b>«Сканувати QR-код»</b> або{" "}
                    <b>«Ввести ключ вручну»</b>.
                  </li>
                  <li>
                    Додайте акаунт <b>Cvanto</b>, а потім введіть 6-значний код із
                    застосунку нижче.
                  </li>
                </ol>
              </aside>

              <div className="setup-pane">
                <div className="left">
                  <div className="qr-card">
                    <p className="qr-caption">
                      Скануйте QR-код або введіть ключ вручну в Authenticator, щоб
                      додати Cvanto.
                    </p>
                    {otpUri && (
                      <div className="qr-wrap" aria-label="QR-код для сканування">
                        <QRCodeCanvas value={otpUri} size={220} />
                      </div>
                    )}
                    <div className="secret-row">
                      <div className="secret-label">Ключ (base32):</div>
                      <div className="secret-value">
                        <code>{secret}</code>
                        <button
                          type="button"
                          className="btn ghost small"
                          onClick={copySecret}
                          aria-label="Скопіювати ключ"
                        >
                          {copied ? "Скопійовано" : "Копіювати"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <aside className="right">
                  <div className="otp-form">
                    <label htmlFor="otp" className="otp-label">
                      Введіть 6-значний код з додатку
                    </label>
                    <input
                      id="otp"
                      className="otp-input"
                      type="text"
                      inputMode="numeric"
                      pattern="\d{6}"
                      maxLength={6}
                      placeholder="••••••"
                      value={code}
                      onChange={(e) =>
                        setCode(e.target.value.replace(/\D/g, ""))
                      }
                      autoFocus
                    />
                    <p className="otp-hint">
                      Код змінюється щохвилини. Введіть актуальний код з Authenticator
                      і натисніть кнопку нижче, щоб прив’язати другий фактор до
                      вашого акаунта.
                    </p>
                    <div className="actions vertical">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={goBackToStatus}
                        disabled={loading}
                      >
                        ← Назад
                      </button>
                      <button
                        className="btn primary"
                        onClick={verifyAndEnable}
                        disabled={loading || code.length < 6}
                        aria-busy={loading}
                      >
                        {loading ? "Перевіряю…" : "Підтвердити та увімкнути"}
                      </button>
                    </div>
                  </div>
                </aside>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default MultiFactorAuth;
